import { createContext, useContext, useMemo, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import type {
  DatePickerContentProps,
  DatePickerProps,
  DatePickerTriggerProps,
} from './DatePicker.types';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@/shared/lib/merge-classes';
import { resolveDateFnsLocale } from '@/shared/i18n/date-fns-locale';

/**
 * Phase 6 — `DatePicker` compound (Shadcn-canonical).
 *
 * Composes v4 `<Popover>` + `<Calendar>` (react-day-picker) into a
 * dot-notation compound that replaces the MUI X DatePicker hidden inside
 * `DateTextField`:
 *
 * @example
 * ```tsx
 * <DatePicker value={date} onChange={setDate}>
 *   <DatePicker.Trigger placeholder="Add dates" />
 *   <DatePicker.Content />
 * </DatePicker>
 * ```
 *
 * Open state is INTERNAL (uncontrolled) so consumers don't have to thread
 * `open`/`onOpenChange` plumbing. If you need controlled open state, drop
 * the compound and use `<Popover>` + `<Calendar>` directly.
 *
 * Reference compound: `FeatureSidebar` (compound + dot-notation slots).
 * Reference Shadcn pattern: https://ui.shadcn.com/docs/components/date-picker
 */

interface DatePickerContextValue {
  value: Date | null;
  onChange: (next: Date | null) => void;
  disabled: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  id?: string;
}

const DatePickerContext = createContext<DatePickerContextValue | null>(null);

function useDatePickerContext(component: string): DatePickerContextValue {
  const ctx = useContext(DatePickerContext);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <DatePicker>`);
  }
  return ctx;
}

function DatePickerRoot({ value, onChange, disabled = false, id, children }: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const contextValue = useMemo<DatePickerContextValue>(
    () => ({ value, onChange, disabled, open, setOpen, id }),
    [value, onChange, disabled, open, id],
  );

  return (
    <DatePickerContext.Provider value={contextValue}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </DatePickerContext.Provider>
  );
}

function DatePickerTrigger({
  placeholder,
  className,
  'aria-describedby': ariaDescribedBy,
}: DatePickerTriggerProps) {
  const { t, i18n } = useTranslation('common');

  const { value, disabled, id } = useDatePickerContext('DatePicker.Trigger');

  return (
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        id={id}
        aria-describedby={ariaDescribedBy}
        className={mergeClasses(
          'w-full justify-start gap-2 font-normal data-[empty=true]:text-muted-foreground',
          className,
        )}
        data-empty={value === null ? 'true' : 'false'}
      >
        <CalendarIcon className="size-md" />
        {value === null ? (
          <span>{placeholder ?? t('ui.datePicker.pickADate')}</span>
        ) : (
          <span>{format(value, 'PPP', { locale: resolveDateFnsLocale(i18n.language) })}</span>
        )}
      </Button>
    </PopoverTrigger>
  );
}

function DatePickerContent({
  side,
  sideOffset = 4,
  startMonth,
  endMonth,
  disabledDates,
}: DatePickerContentProps) {
  const { value, onChange, setOpen } = useDatePickerContext('DatePicker.Content');

  return (
    <PopoverContent
      // pointer-events-auto so the calendar remains clickable when this
      // DatePicker renders inside a Radix Dialog with `modal=true`
      // (which sets `body { pointer-events: none }` on the document body
      // and only re-enables it on DialogOverlay/DialogContent — portals
      // outside the Dialog tree inherit `none`). Harmless outside a
      // Modal Dialog. See Phase 6 follow-up.
      className="pointer-events-auto w-auto p-0"
      align="start"
      side={side}
      sideOffset={sideOffset}
    >
      <Calendar
        mode="single"
        // Renders month + year as dropdowns in the caption so users can jump
        // across months/years without paging one at a time. react-day-picker
        // defaults the year range to (today − 100yr) → end of current year;
        // pass `startMonth`/`endMonth` from the call site when a different
        // range is required.
        captionLayout="dropdown"
        startMonth={startMonth}
        endMonth={endMonth}
        disabled={disabledDates}
        selected={value ?? undefined}
        onSelect={(next) => {
          onChange(next ?? null);
          if (next) setOpen(false);
        }}
        defaultMonth={value ?? undefined}
      />
    </PopoverContent>
  );
}

DatePickerTrigger.displayName = 'DatePicker.Trigger';
DatePickerContent.displayName = 'DatePicker.Content';

export const DatePicker = Object.assign(DatePickerRoot, {
  Trigger: DatePickerTrigger,
  Content: DatePickerContent,
});
